import React from 'react';

import { Box, Divider, FormControlLabel, Typography } from '@material-ui/core';

import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { TimeAndAttendanceLocation } from '@vyce/core/src/types';

import useStyles from '../../styles';
import { TargetCheckboxGrid, TargetGridItem } from './TargetCheckboxGrid';

interface Props {
  locations: TimeAndAttendanceLocation[];
  selectedLocationIds: string[];
  shiftIdsByLocation: Record<string, string[]>;
  /** Selected locations that still have no shift ticked */
  missingShiftIds: string[];
  onToggleLocation: (id: string, checked: boolean) => void;
  onToggleAllLocations: (checked: boolean) => void;
  onToggleShift: (locationId: string, shiftId: string, checked: boolean) => void;
  onToggleAllShifts: (checked: boolean) => void;
}

const activeShiftsOf = (location: TimeAndAttendanceLocation) =>
  (location.shifts ?? []).filter(shift => !!shift.uuid && !shift.deleted_at);

export const TimeTargetSection: React.FC<Props> = ({
  locations,
  selectedLocationIds,
  shiftIdsByLocation,
  missingShiftIds,
  onToggleLocation,
  onToggleAllLocations,
  onToggleShift,
  onToggleAllShifts,
}) => {
  const classes = useStyles();

  // A worker is always added to a shift, so a location without shifts cannot be a target
  const locationItems: TargetGridItem[] = locations.map(location => {
    const hasShifts = activeShiftsOf(location).length > 0;
    return {
      id: location.uuid as string,
      name: location.name,
      disabled: !hasShifts,
      hint: hasShifts ? undefined : 'no shifts',
    };
  });

  const selectedLocations = locations.filter(location =>
    selectedLocationIds.includes(location.uuid as string)
  );
  let allShiftsCount = 0;
  let selectedShiftsCount = 0;
  selectedLocations.forEach(location => {
    allShiftsCount += activeShiftsOf(location).length;
    selectedShiftsCount += shiftIdsByLocation[location.uuid as string]?.length ?? 0;
  });

  return (
    <>
      <Typography className={classes.detailsTitle}>Which locations?</Typography>
      <TargetCheckboxGrid
        items={locationItems}
        selectedIds={selectedLocationIds}
        onToggle={onToggleLocation}
        allLabel="All locations"
        onToggleAll={onToggleAllLocations}
      />

      {selectedLocations.length > 0 && (
        <Box marginTop={2}>
          <Divider style={{ marginBottom: 12 }} />

          <Box marginLeft="-6px">
            <FormControlLabel
              classes={{ label: classes.detailsTitle }}
              style={{ marginRight: '24px' }}
              control={
                <AppCheckbox
                  checked={allShiftsCount > 0 && selectedShiftsCount === allShiftsCount}
                  onChange={event => onToggleAllShifts(event.target.checked)}
                  variant="rectangle"
                  color="primary"
                />
              }
              label={selectedLocations.length > 1 ? 'All shifts on all selected locations' : 'All shifts'}
            />
          </Box>

          {selectedLocations.map(location => {
            const locationId = location.uuid as string;
            const missing = missingShiftIds.includes(locationId);
            return (
              <Box key={locationId} marginTop={1}>
                <Typography className={classes.detailsTitle}>
                  Which shift on {location.name}?
                  {missing && <span className={classes.sectionError}> · choose at least one</span>}
                </Typography>
                <TargetCheckboxGrid
                  items={activeShiftsOf(location).map(shift => ({
                    id: shift.uuid as string,
                    name: shift.name,
                  }))}
                  selectedIds={shiftIdsByLocation[locationId] ?? []}
                  onToggle={(shiftId, checked) => onToggleShift(locationId, shiftId, checked)}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};
